import { useEffect, useState } from "react";
import { Link } from "wouter";
import { ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  analyticsConsentChangedEvent,
  getConsentPreferences,
  openAnalyticsSettingsEvent,
  setConsentPreferences,
  type ConsentPreferences,
} from "@/lib/analytics";

export function AnalyticsConsentBanner() {
  const initialPreferences = getConsentPreferences();
  const [isOpen, setIsOpen] = useState(() => initialPreferences === null);
  const [analytics, setAnalytics] = useState(
    () => initialPreferences?.analytics ?? false,
  );
  const [marketing, setMarketing] = useState(
    () => initialPreferences?.marketing ?? false,
  );

  useEffect(() => {
    const openSettings = () => {
      const current = getConsentPreferences();
      setAnalytics(current?.analytics ?? false);
      setMarketing(current?.marketing ?? false);
      setIsOpen(true);
    };
    const closeSettings = () => setIsOpen(false);

    window.addEventListener(openAnalyticsSettingsEvent, openSettings);
    window.addEventListener(analyticsConsentChangedEvent, closeSettings);
    return () => {
      window.removeEventListener(openAnalyticsSettingsEvent, openSettings);
      window.removeEventListener(analyticsConsentChangedEvent, closeSettings);
    };
  }, []);

  const save = (preferences: ConsentPreferences) => {
    setConsentPreferences(preferences);
    setIsOpen(false);
  };

  if (!isOpen) return null;

  return (
    <aside
      aria-label="Cookievoorkeuren"
      className="fixed inset-x-3 bottom-3 z-[100] mx-auto max-w-3xl rounded-2xl border border-border bg-background p-5 shadow-2xl sm:inset-x-6 sm:p-6"
      data-testid="analytics-consent-banner"
    >
      <div className="flex items-start gap-4">
        <div className="hidden h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary sm:flex">
          <ShieldCheck className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="font-serif text-lg font-semibold text-foreground">
            Jouw cookievoorkeuren
          </h2>
          <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
            Kies apart of we analytische cookies en Meta Pixel voor
            conversiemeting mogen gebruiken. Zonder marketingtoestemming laden
            we Meta niet. Lees meer in ons{" "}
            <Link href="/privacy" className="font-medium text-primary hover:underline">
              privacybeleid
            </Link>
            .
          </p>
          <div className="mt-4 space-y-3 rounded-xl border border-border p-3">
            <label className="flex items-center justify-between gap-4">
              <span>
                <span className="block text-sm font-medium text-foreground">
                  Analytische cookies
                </span>
                <span className="block text-xs text-muted-foreground">
                  Google Analytics helpt ons de app te verbeteren.
                </span>
              </span>
              <Switch
                checked={analytics}
                onCheckedChange={setAnalytics}
                data-testid="analytics-consent-toggle"
                aria-label="Analytische cookies toestaan"
              />
            </label>
            <label className="flex items-center justify-between gap-4">
              <span>
                <span className="block text-sm font-medium text-foreground">
                  Marketing en conversiemeting
                </span>
                <span className="block text-xs text-muted-foreground">
                  Meta Pixel meet alleen registratie en gestarte checkout.
                </span>
              </span>
              <Switch
                checked={marketing}
                onCheckedChange={setMarketing}
                data-testid="marketing-consent-toggle"
                aria-label="Marketing en conversiemeting toestaan"
              />
            </label>
          </div>
          <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => save({ analytics: false, marketing: false })}
              data-testid="analytics-consent-deny"
            >
              Alleen noodzakelijk
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => save({ analytics, marketing })}
              data-testid="consent-save-selection"
            >
              Keuzes opslaan
            </Button>
            <Button
              type="button"
              onClick={() => save({ analytics: true, marketing: true })}
              data-testid="analytics-consent-accept"
            >
              Alles accepteren
            </Button>
          </div>
        </div>
      </div>
    </aside>
  );
}